import { lazy, useEffect } from 'react'
import history from 'shared/utils/history'
import map from 'lodash/map'
import omit from 'lodash/omit'
import {
  CAN_CREATE_PARTICIPANT_PROFILES,
  CAN_VIEW_PARTICIPANT_LIST,
  CAN_VIEW_CHECK_IN_OUT_MEMBERS,
  CAN_CREATE_POINTS,
  CAN_CREATE_SURVEY,
  CAN_EXPORT_SURVEY_RESULTS,
  CAN_VIEW_REPORTS,
  CAN_CREATE_COMMUNICATIONS,
  CAN_VIEW_SCHEDULE,
  CAN_VIEW_PROGRAMS,
  CAN_VIEW_EVENTS,
  IS_VIEW_ASSIGNED_ONLY,
  CAN_VIEW_INBOX,
  CAN_VIEW_CHALLENGES,
  CAN_VIEW_STORE,
  CAN_TAKE_ATTENDANCE,
  CAN_MANAGE_PARENT,
} from 'shared/constants/permission'
import { ORGANIZATION_TYPE_HUDDLE_UP, ORGANIZATION_TYPE_DEFAULT } from 'shared/constants/organizationSetting'
import { FEATURE_VIEW_REPORTS, FEATURE_COMMUNICATION, FEATURE_TIER } from 'shared/constants/features'
import { PARTICIPANT, COACH, ADMIN, ORGANIZATION, GRANTER, SUPER_ADMIN, KIOSK } from 'shared/hocs/withAuth'

const ViewWaiver = lazy(() => import('pages/ViewWaiver'))
const SignWaiver = lazy(() => import('pages/SignWaiver'))
const ParticipantInvitation = lazy(() => import('pages/Participants/containers/ParticipantInvitation'))
const CoachInvitation = lazy(() => import('pages/Coach/containers/CoachInvitation'))
const PublicInviteParticipantRequest = lazy(() =>
  import('pages/Participants/containers/PublicInviteParticipantRequest'),
)
const PublicInviteCoachRequest = lazy(() => import('pages/Coach/containers/PublicInviteCoachRequest'))
const ResetPassword = lazy(() => import('pages/ResetPassword'))
const Login = lazy(() => import('pages/Login/containers/LoginPage'))
const ForgotPassword = lazy(() => import('pages/Login/containers/ForgotPasswordPage'))
const HomePage = lazy(() => import('pages/Home'))
const CustomizeScoreboard = lazy(() => import('pages/CustomizeScoreboard'))
const Participant = lazy(() => import('pages/Participants'))
const ProgramsEvents = lazy(() => import('pages/ProgramsEvents'))
const PointsRewards = lazy(() => import('pages/Points'))
const MyProfile = lazy(() => import('pages/MyProfile'))
const Points = lazy(() => import('pages/Points'))
const Badges = lazy(() => import('pages/Badges'))
const Survey = lazy(() => import('pages/Survey'))
const CheckIn = lazy(() => import('pages/CheckIn'))
const Challenges = lazy(() => import('pages/Challenges'))
const Communications = lazy(() => import('pages/Communications'))
const Waivers = lazy(() => import('pages/Waivers'))
const Inbox = lazy(() => import('pages/Inbox'))
const Store = lazy(() => import('pages/Store'))
const Report = lazy(() => import('pages/Report'))
const User = lazy(() => import('pages/User'))
const Organization = lazy(() => import('pages/Organization'))
const Requests = lazy(() => import('pages/Requests'))
const Granters = lazy(() => import('pages/Granters'))
const Resource = lazy(() => import('pages/Resource'))
const Resources = lazy(() => import('pages/Resources'))
const Modules = lazy(() => import('pages/Modules'))
const UserRole = lazy(() => import('pages/UserRole'))
const Parent = lazy(() => import('pages/Parent'))
const Flagged = lazy(() => import('pages/Flagged'))
const NotFound = lazy(() => import('pages/NotFound'))
const Unsubscribe = lazy(() => import('pages/Unsubscribe'))

export function NotFoundAppLevel() {
  useEffect(() => {
    history.replace(`/404-page-not-found`)
  }, [])

  return null
}

const ROUTES = [
  {
    key: 'login',
    component: Login,
    path: '/login',
    exact: true,
    pageProps: {
      hasNavigation: false,
      requireAuth: false,
      pageTitle: 'Login',
      pageDescription: 'Login',
      permissions: [],
      orgTypes: [ORGANIZATION_TYPE_HUDDLE_UP],
      level: null,
    },
  },
  {
    key: 'forgot-password',
    component: ForgotPassword,
    path: '/forgot-password',
    exact: true,
    pageProps: {
      hasNavigation: false,
      requireAuth: false,
      permissions: [],
      orgTypes: [ORGANIZATION_TYPE_HUDDLE_UP],
      level: null,
    },
  },
  {
    key: 'reset-password',
    component: ResetPassword,
    path: '/reset-password',
    exact: true,
    pageProps: {
      hasNavigation: false,
      requireAuth: 'optional',
      permissions: [],
      orgTypes: [ORGANIZATION_TYPE_HUDDLE_UP],
      level: null,
      // requiredRoles: ['Organization', 'Admin', 'Participant', 'Coach'],
    },
  },
  {
    key: 'sign-waiver',
    component: SignWaiver,
    path: '/sign-waiver/:id',
    exact: true,
    pageProps: {
      hasNavigation: false,
      requireAuth: 'optional',
      permissions: [],
      orgTypes: [ORGANIZATION_TYPE_HUDDLE_UP],
      level: null,
      // requiredRoles: ['Organization', 'Admin', 'Participant', 'Coach'],
    },
  },
  {
    key: 'participant-invitation',
    component: ParticipantInvitation,
    path: '/invite/:id',
    exact: true,
    pageProps: {
      hasNavigation: false,
      requireAuth: 'optional',
      permissions: [],
      level: null,
    },
  },
  {
    key: 'coach-invitation',
    component: CoachInvitation,
    path: '/user/invite/:id',
    exact: true,
    pageProps: {
      hasNavigation: false,
      requireAuth: 'optional',
      permissions: [],
      orgTypes: [ORGANIZATION_TYPE_HUDDLE_UP],
      level: null,
    },
  },
  {
    key: 'public-participant-invitation-request',
    component: PublicInviteParticipantRequest,
    path: '/invitation/request',
    exact: true,
    pageProps: {
      hasNavigation: false,
      requireAuth: false,
      pageTitle: 'Membership Request',
      pageDescription: 'Membership Request',
      permissions: [],
      level: null,
    },
  },
  {
    key: 'public-coach-invitation-request',
    component: PublicInviteCoachRequest,
    path: '/invitation/teacher/request',
    exact: true,
    pageProps: {
      hasNavigation: false,
      requireAuth: false,
      pageTitle: 'Teacher Membership Request',
      pageDescription: 'Teacher Membership Request',
      permissions: [],
      orgTypes: [ORGANIZATION_TYPE_HUDDLE_UP],
      orgExclusiveTypes: [ORGANIZATION_TYPE_HUDDLE_UP],
      level: null,
    },
  },
  {
    key: 'view-waiver',
    component: ViewWaiver,
    path: '/view-waiver/:id',
    exact: true,
    pageProps: {
      hasNavigation: false,
      requireAuth: 'optional',
      permissions: [],
      orgTypes: [ORGANIZATION_TYPE_HUDDLE_UP],
      level: null,
    },
  },
  {
    key: 'home',
    component: HomePage,
    path: '/',
    exact: true,
    pageProps: {
      isKioskAllowed: true,
      hasNavigation: true,
      requireAuth: true,
      orgTypes: [ORGANIZATION_TYPE_HUDDLE_UP],
      pageTitle: 'Home',
      pageDescription: 'Home',
      permissions: [],
      level: {
        up: PARTICIPANT,
        equal: PARTICIPANT,
        down: null,
      },
      // requiredRoles: ['Organization', 'Admin', 'Participant', 'Coach'],
    },
  },
  {
    key: 'customize',
    component: CustomizeScoreboard,
    path: '/customize',
    pageProps: {
      hasNavigation: true,
      requireAuth: true,
      pageTitle: 'Customize Scoreboard',
      pageDescription: 'Customize Scoreboard Settings',
      permissions: [],
      orgTypes: [ORGANIZATION_TYPE_HUDDLE_UP],
      requireWaiver: true,
      level: {
        up: ORGANIZATION,
        equal: [ORGANIZATION],
        down: ORGANIZATION,
      },
    },
  },
  {
    key: 'participant',
    component: Participant,
    path: '/participants',
    pageProps: {
      requireAuth: true,
      pageTitle: 'Participant',
      permissions: [CAN_VIEW_PARTICIPANT_LIST, IS_VIEW_ASSIGNED_ONLY],
      level: {
        up: PARTICIPANT,
        equal: PARTICIPANT,
        down: null,
      },
      // requiredRoles: ['Organization', 'Admin', 'Coach'],
    },
  },
  {
    key: 'student',
    component: Participant,
    path: '/students',
    pageProps: {
      requireAuth: true,
      pageTitle: 'Student',
      permissions: [],
      orgTypes: [ORGANIZATION_TYPE_HUDDLE_UP],
      orgExclusiveTypes: [ORGANIZATION_TYPE_HUDDLE_UP],
      requireWaiver: true,
      level: {
        up: PARTICIPANT,
        equal: [PARTICIPANT, COACH],
        down: null,
      },
      // requiredRoles: ['Organization', 'Admin', 'Coach'],
    },
  },
  {
    key: 'parent',
    component: Parent,
    path: '/user/parent',
    pageProps: {
      requireAuth: true,
      pageTitle: 'Parent',
      permissions: [CAN_MANAGE_PARENT],
      level: {
        up: null,
        equal: [PARTICIPANT, ADMIN],
        down: null,
      },
      // requiredRoles: ['Organization', 'Admin'],
    },
  },
  {
    key: 'flagged',
    component: Flagged,
    path: '/user/flagged',
    pageProps: {
      requireAuth: true,
      pageTitle: 'Flagged Members',
      permissions: [CAN_VIEW_PARTICIPANT_LIST],
      level: {
        up: ADMIN,
        equal: ADMIN,
        down: null,
      },
      // requiredRoles: ['Organization', 'Admin'],
    },
  },
  {
    key: 'user',
    component: User,
    path: '/user/:role_id?',
    pageProps: {
      requireAuth: true,
      pageTitle: 'User',
      permissions: [],
      orgTypes: [ORGANIZATION_TYPE_HUDDLE_UP],
      requireWaiver: true,
      level: {
        up: ADMIN,
        equal: ADMIN,
        down: null,
      },
      // requiredRoles: ['Organization', 'Admin'],
    },
  },
  {
    key: 'requests',
    component: Requests,
    path: '/requests',
    pageProps: {
      requireAuth: true,
      pageTitle: 'User',
      permissions: [],
      level: {
        up: SUPER_ADMIN,
        equal: [SUPER_ADMIN],
        down: null,
      },
    },
  },
  {
    key: 'granters',
    component: Granters,
    path: '/granters',
    pageProps: {
      requireAuth: true,
      pageTitle: 'User',
      permissions: [],
      level: {
        up: SUPER_ADMIN,
        equal: [SUPER_ADMIN],
        down: null,
      },
    },
  },
  {
    key: 'organization',
    component: Organization,
    path: '/organization',
    pageProps: {
      requireAuth: true,
      pageTitle: 'User',
      permissions: [],
      level: {
        up: GRANTER,
        equal: [GRANTER, SUPER_ADMIN],
        down: null,
      },
      // requiredRoles: ['Organization', 'Admin'],
    },
  },
  {
    key: 'role',
    component: UserRole,
    path: '/role',
    pageProps: {
      requireAuth: true,
      pageTitle: 'UserRole',
      permissions: [],
      orgTypes: [ORGANIZATION_TYPE_HUDDLE_UP],
      requireWaiver: true,
      level: {
        up: null,
        equal: ORGANIZATION,
        down: null,
      },
      // requiredRoles: ['Organization', 'Admin'],
    },
  },
  {
    key: 'resource',
    component: Resource,
    path: '/resource',
    pageProps: {
      requireAuth: true,
      pageTitle: 'Resource',
      permissions: [],
      level: {
        up: null,
        equal: ORGANIZATION,
        down: null,
      },
      // requiredRoles: ['Organization', 'Admin'],
    },
  },
  {
    key: 'resources-huddle-up',
    component: Resources,
    path: '/resources',
    pageProps: {
      requireAuth: true,
      pageTitle: 'Resources',
      permissions: [],
      level: {
        up: PARTICIPANT,
        equal: [ADMIN, ORGANIZATION, PARTICIPANT, COACH],
        down: ORGANIZATION,
        // up: null,
        // equal: ORGANIZATION,
        // down: null,
      },
      orgTypes: [ORGANIZATION_TYPE_HUDDLE_UP],
      orgExclusiveTypes: [ORGANIZATION_TYPE_HUDDLE_UP],
      requireWaiver: true,
      // requiredRoles: ['Organization', 'Admin'],
    },
  },
  {
    key: 'modules-huddle-up',
    component: Modules,
    path: '/modules',
    pageProps: {
      requireAuth: true,
      pageTitle: 'Modules',
      permissions: [],
      level: {
        up: PARTICIPANT,
        equal: [PARTICIPANT, COACH, ORGANIZATION, ADMIN],
        down: ORGANIZATION,
      },
      orgTypes: [ORGANIZATION_TYPE_HUDDLE_UP],
      orgExclusiveTypes: [ORGANIZATION_TYPE_HUDDLE_UP],
      requireWaiver: true,
    },
  },
  {
    key: 'check-in',
    component: CheckIn,
    path: '/check-in',
    pageProps: {
      requireAuth: true,
      pageTitle: 'Checkins',
      isKioskAllowed: true,
      permissions: [CAN_VIEW_CHECK_IN_OUT_MEMBERS],
      level: {
        up: COACH,
        equal: COACH,
        down: null,
      },
      // requiredRoles: ['Organization', 'Admin', 'Coach'],
    },
  },
  {
    key: 'programs-events',
    component: ProgramsEvents,
    path: '/programs-events',
    pageProps: {
      hasNavigation: true,
      requireAuth: true,
      pageTitle: 'Programs & Events',
      permissions: [CAN_VIEW_PROGRAMS, CAN_VIEW_EVENTS, CAN_VIEW_SCHEDULE, CAN_TAKE_ATTENDANCE],
      isKioskAllowed: true,
      level: {
        up: PARTICIPANT,
        equal: PARTICIPANT,
        down: null,
      },
      // requiredRoles: ['Organization', 'Admin', 'Coach', 'Participant'],
    },
  },
  {
    key: 'points-rewards',
    component: PointsRewards,
    path: '/points-rewards',
    pageProps: {
      hasNavigation: true,
      requireAuth: true,
      pageTitle: 'Points & Rewards',
      permissions: [CAN_CREATE_POINTS],
      level: {
        up: COACH,
        equal: [COACH, ORGANIZATION, ADMIN],
        down: ORGANIZATION,
      },
      // requiredRoles: ['Organization', 'Admin', 'Coach'],
    },
  },
  {
    key: 'challenges',
    component: Challenges,
    path: '/challenges',
    pageProps: {
      hasNavigation: true,
      requireAuth: true,
      pageTitle: 'Challenges',
      permissions: [],
      level: {
        up: PARTICIPANT,
        equal: PARTICIPANT,
        down: null,
      },
      // requiredRoles: ['Participant', 'Organization', 'Admin'],
    },
  },
  {
    key: 'badges',
    component: Badges,
    path: '/badge',
    pageProps: {
      hasNavigation: true,
      requireAuth: true,
      features: [FEATURE_TIER],
      pageTitle: 'Badge',
      pageDescription: 'Badge',
      permissions: [],
      level: {
        up: ADMIN,
        equal: [ORGANIZATION, ADMIN],
        down: ORGANIZATION,
      },
      // requiredRoles: ['Organization', 'Admin'],
    },
  },
  {
    key: 'survey',
    component: Survey,
    path: '/survey',
    pageProps: {
      hasNavigation: true,
      requireAuth: true,
      pageTitle: 'Survey',
      pageDescription: 'Survey',
      permissions: [CAN_CREATE_SURVEY, CAN_EXPORT_SURVEY_RESULTS],
      level: {
        up: COACH,
        equal: COACH,
        down: null,
      },
      // requiredRoles: ['Organization', 'Admin'],
    },
  },
  {
    key: 'my-profile',
    component: MyProfile,
    path: '/my-profile',
    pageProps: {
      hasNavigation: true,
      requireAuth: true,
      pageTitle: 'My Profile',
      permissions: [],
      orgTypes: [ORGANIZATION_TYPE_HUDDLE_UP],
      requireWaiver: true,
      level: {
        up: PARTICIPANT,
        equal: PARTICIPANT,
        down: null,
      },
      // requiredRoles: ['Organization', 'Admin', 'Participant', 'Coach'],
    },
  },
  {
    key: 'communications',
    component: Communications,
    path: '/communications',
    pageProps: {
      hasNavigation: true,
      requireAuth: true,
      pageTitle: 'Communications',
      features: [FEATURE_COMMUNICATION],
      permissions: [CAN_CREATE_COMMUNICATIONS],
      orgTypes: [ORGANIZATION_TYPE_HUDDLE_UP],
      requireWaiver: true,
      level: {
        up: COACH,
        equal: [COACH, ORGANIZATION, ADMIN],
        down: ORGANIZATION,
      },
      // requiredRoles: ['Organization', 'Admin'],
    },
  },
  {
    key: 'Waivers',
    component: Waivers,
    path: '/waivers',
    pageProps: {
      hasNavigation: true,
      hasFooter: true,
      requireAuth: true,
      pageTitle: 'Waivers',
      permissions: [],
      orgTypes: [ORGANIZATION_TYPE_HUDDLE_UP],
      requireWaiver: true,
      level: {
        up: null,
        equal: ORGANIZATION,
        down: null,
      },
      // requiredRoles: ['Organization', 'Admin'],
    },
  },
  {
    key: 'messages',
    component: Inbox,
    path: '/messages',
    pageProps: {
      hasNavigation: true,
      requireAuth: true,
      pageTitle: 'Inbox',
      permissions: [CAN_VIEW_INBOX],
      orgTypes: [ORGANIZATION_TYPE_HUDDLE_UP],
      requireWaiver: true,
      level: {
        up: PARTICIPANT,
        equal: PARTICIPANT,
        down: null,
      },
      // requiredRoles: ['Coach', 'Participant'],
    },
  },
  {
    key: 'inbox',
    component: Inbox,
    path: '/inbox',
    pageProps: {
      hasNavigation: true,
      requireAuth: true,
      pageTitle: 'Inbox',
      permissions: [CAN_VIEW_INBOX],
      level: {
        up: PARTICIPANT,
        equal: PARTICIPANT,
        down: null,
      },
      // requiredRoles: ['Coach', 'Participant'],
    },
  },
  {
    key: 'store',
    component: Store,
    path: '/store',
    pageProps: {
      hasNavigation: true,
      requireAuth: true,
      pageTitle: 'Store',
      permissions: [CAN_VIEW_STORE],
      level: {
        up: PARTICIPANT,
        equal: PARTICIPANT,
        down: null,
      },
      // requiredRoles: ['Organization', 'Admin', 'Participant'],
    },
  },
  {
    key: 'reports',
    component: Report,
    path: '/reports',
    pageProps: {
      hasNavigation: true,
      requireAuth: true,
      pageTitle: 'Reports',
      features: [FEATURE_VIEW_REPORTS],
      permissions: [CAN_VIEW_REPORTS],
      level: {
        up: ADMIN,
        equal: [ORGANIZATION, ADMIN],
        down: ORGANIZATION,
      },
    },
  },
  {
    key: 'help',
    component: () => {
      window.open(
        'https://form.asana.com/?hash=28d540b69d7ec4bca649e15025d49e530864d6b0f8f5391714f70822f726372f&id=1185519460971154',
        '_self',
        'noopener,noreferrer',
      )
      return null
    },
    path: '/help',
    pageProps: {
      hasNavigation: true,
      requireAuth: true,
      pageTitle: 'Help',
      permissions: [],
      orgTypes: [ORGANIZATION_TYPE_HUDDLE_UP],
      requireWaiver: true,
      level: {
        up: PARTICIPANT,
        equal: PARTICIPANT,
        down: null,
      },
    },
  },
  {
    key: 'unsubscribe',
    component: Unsubscribe,
    path: '/unsubscribe/:id',
    exact: true,
    pageProps: {
      hasNavigation: false,
      requireAuth: 'optional',
      pageTitle: 'Unsubscribe',
      pageDescription: 'Unsubscribe to email newsletter.',
      permissions: [],
      orgTypes: [ORGANIZATION_TYPE_HUDDLE_UP],
      level: null,
    },
  },
  {
    key: '404-page',
    component: NotFound,
    path: '/404-page-not-found',
    pageProps: {
      hasNavigation: false,
      requireAuth: 'optional',
      pageTitle: '404 - Page Not Found',
      pageDescription: 'Page Not Found',
      permissions: [],
      orgTypes: [ORGANIZATION_TYPE_HUDDLE_UP],
      level: null,
    },
  },
  {
    key: 'not-found-app-level',
    component: NotFoundAppLevel,
    path: '*',
    pageProps: {
      hasNavigation: false,
      requireAuth: 'optional',
      pageTitle: 'Page Not Found',
      pageDescription: 'Page Not Found',
      permissions: [],
      orgTypes: [ORGANIZATION_TYPE_HUDDLE_UP],
      level: null,
    },
  },
]

export default ROUTES
export const routes = map(ROUTES, route => omit(route, ['component']))
